/**
 * Facts derived from an ad asset's storage key or URL.
 */

/**
 * The Content-Type an ad component asset must be *forced* to serve as, or
 * `undefined` if none is needed. Single source of truth for the rule.
 *
 * Agent-uploaded assets are stored as `application/octet-stream` (their
 * presigned PUT can't sign a type). Browsers won't paint a non-`image/svg+xml`
 * SVG in an `<img>`/`<image>`. Raster formats are content-sniffed, so they're
 * exempt. `.html` scene files are deliberately NOT forced to `text/html`:
 * every consumer reads them via `fetch()` (Content-Type is irrelevant there),
 * and serving agent-authored HTML renderable from a presigned URL would turn
 * that URL into a script-execution surface.
 */
export function forcedImageContentTypeForKey(key: string): string | undefined {
  return key.toLowerCase().endsWith(".svg") ? "image/svg+xml" : undefined;
}

/**
 * Raster extensions the CloudFront image host / imgproxy can serve.
 * SVG is excluded because CloudFront cannot override Content-Type, so it
 * already takes the presigned-S3 path via `forcedImageContentTypeForKey`.
 * Non-image keys the preview fetches (.js, .json, .html) must also stay
 * off this host: the image CDN / WAF returns 403 for them.
 */
const IMAGE_CDN_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".avif",
  ".heic",
  ".heif",
  ".bmp",
  ".tif",
  ".tiff",
] as const;

/**
 * Whether GET /agents/files may sign this key onto the CloudFront image host.
 * False for custom-component JS/schema JSON, HTML scenes, fonts, and SVG.
 */
export function isImageCdnKey(key: string): boolean {
  const lower = key.toLowerCase();
  return IMAGE_CDN_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * The private-content storage key an ad `asset_url` points at, or null when the
 * URL isn't one of ours to read that way.
 *
 * An `asset_url` may be a GCS URL, an expired presigned S3/MinIO URL, or a
 * session-gated `/api/agents/ad-artifacts` link — none dependably fetchable
 * server-side, but all three carry the storage key, so reading through the
 * private-content bucket works where fetching the URL does not.
 *
 * Matched on the `{workspaceId}/ad-artifacts/...` prefix every ad render is
 * written under (see `uploadImageToObjectStorage`). The query string is excluded
 * so a presigned URL yields the same key as a plain one.
 */
export function adArtifactStorageKeyFromUrl(assetUrl: string): string | null {
  const match = assetUrl.match(/\/(\d+\/ad-artifacts\/[^?]+)/);

  return match?.[1] ?? null;
}
